import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

const REACT_APP_API_URL = `${process.env.REACT_APP_API_URL}`;

/* ─────── Types ─────── */

export interface DailyPlanItem {
  id: number;
  user_id: number;
  company_id: number;
  title: string;
  description?: string;
  target_url?: string;
  target_label?: string;
  plan_date: string;
  is_completed: boolean;
  completed_at?: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface DailyPlanResponse {
  results: DailyPlanItem[];
  plan_date: string;
}

/* ─────── Hooks ─────── */

export const useDailyPlan = (planDate?: string, onError?: any) => {
  const fetchData = async () => {
    const params: any = {};
    if (planDate) params.plan_date = planDate;
    const res = await axios.get<DailyPlanResponse>(`${REACT_APP_API_URL}/daily-plan/list`, { params });
    return res?.data;
  };
  return useQuery(['daily-plan', planDate], fetchData, {
    refetchOnWindowFocus: false,
    onError,
    staleTime: 30000,
    retry: false,
  });
};

export const useAddPlanItem = (onSuccess?: any, onError?: any) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: { title: string; description?: string; target_url?: string; target_label?: string; plan_date?: string; sort_order?: number }) => {
      return axios.post(`${REACT_APP_API_URL}/daily-plan/create`, payload);
    },
    {
      onError,
      onSuccess: (...args: any[]) => {
        queryClient.invalidateQueries(['daily-plan']);
        queryClient.invalidateQueries(['my-hub-summary']);
        onSuccess?.(...args);
      },
    },
  );
};

export const useTogglePlanItem = (onSuccess?: any, onError?: any) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: { id: number }) => {
      return axios.post(`${REACT_APP_API_URL}/daily-plan/toggle`, payload);
    },
    {
      onError,
      onSuccess: (...args: any[]) => {
        queryClient.invalidateQueries(['daily-plan']);
        queryClient.invalidateQueries(['my-hub-summary']);
        onSuccess?.(...args);
      },
    },
  );
};

export const useUpdatePlanItem = (onSuccess?: any, onError?: any) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: { id: number; title?: string; description?: string; target_url?: string; target_label?: string; sort_order?: number }) => {
      return axios.post(`${REACT_APP_API_URL}/daily-plan/update`, payload);
    },
    {
      onError,
      onSuccess: (...args: any[]) => {
        queryClient.invalidateQueries(['daily-plan']);
        queryClient.invalidateQueries(['my-hub-summary']);
        onSuccess?.(...args);
      },
    },
  );
};

export const useDeletePlanItem = (onSuccess?: any, onError?: any) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: { id: number }) => {
      return axios.post(`${REACT_APP_API_URL}/daily-plan/delete`, payload);
    },
    {
      onError,
      onSuccess: (...args: any[]) => {
        queryClient.invalidateQueries(['daily-plan']);
        queryClient.invalidateQueries(['my-hub-summary']);
        onSuccess?.(...args);
      },
    },
  );
};

export const useCarryOverItems = (onSuccess?: any, onError?: any) => {
  const queryClient = useQueryClient();
  return useMutation(
    (_payload: Record<string, never>) => {
      return axios.post(`${REACT_APP_API_URL}/daily-plan/carry-over`);
    },
    {
      onError,
      onSuccess: (...args: any[]) => {
        queryClient.invalidateQueries(['daily-plan']);
        queryClient.invalidateQueries(['my-hub-summary']);
        onSuccess?.(...args);
      },
    },
  );
};
